import { Fragment } from "react";
import { AlertTriangle, Layers, Pencil, Trash2, XCircle } from "lucide-react";
import { Criterion, PlanBadge } from "./ui";

const WARN_ICON = { shadowed: Layers, tie: Layers, invalid: XCircle };

function Products({ products, byId }) {
  return (
    <div className="space-y-1">
      {products.map((p) => {
        const c = byId[p.id];
        return (
          <div key={p.id} className="flex items-center gap-1.5 text-xs" data-testid={`bs-product-${p.id}`}>
            <PlanBadge plan={p.plan} />
            <span className="truncate text-slate-700" title={p.label}>{c ? c.name : p.label.split("->").slice(1).join("->") || p.label}</span>
            <span className="font-mono text-[10px] text-slate-400">{p.id}</span>
            {!c && <span className="rounded bg-red-50 px-1 text-[10px] font-semibold text-red-600">not in Stax Bill</span>}
            {c && c.status === "Retired" && <span className="rounded bg-slate-100 px-1 text-[10px] font-semibold text-slate-500">retired</span>}
          </div>
        );
      })}
    </div>
  );
}

function Warnings({ list }) {
  return (
    <ul className="mt-2 space-y-1">
      {list.map((w, i) => {
        const Icon = WARN_ICON[w.type] || AlertTriangle;
        return (
          <li key={i} className={`flex items-start gap-1.5 text-xs ${w.type === "invalid" ? "text-red-700" : "text-amber-800"}`} data-testid={`bs-warning-${w.type}`}>
            <Icon className="mt-0.5 h-3.5 w-3.5 shrink-0" />{w.message}
          </li>
        );
      })}
    </ul>
  );
}

export function MappingTable({ rows, ops, byId, can, onEdit, onDelete }) {
  if (!rows.length) return <p className="p-10 text-center text-sm text-slate-500" data-testid="bs-mapping-empty">No rules match.</p>;
  return (
    <table className="w-full text-sm" data-testid="bs-mapping-table">
      <thead className="sticky top-0 z-10 bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500">
        <tr>
          <th className="w-20 px-4 py-2.5 text-left">Priority</th>
          <th className="px-4 py-2.5 text-left">Platform match criteria</th>
          <th className="w-[38%] px-4 py-2.5 text-left">Bills to (Stax Bill product)</th>
          <th className="w-20 px-4 py-2.5" />
        </tr>
      </thead>
      <tbody>
        {rows.map((r) => (
          <Fragment key={r.id}>
            <tr className={`group border-t border-slate-100 align-top transition-colors hover:bg-slate-50/70 ${r.warnings.length ? "shadow-[inset_3px_0_0_#f59e0b]" : ""}`} data-testid={`bs-rule-${r.id}`}>
              <td className="px-4 py-3">
                <div className="font-heading text-base font-semibold tabular-nums text-slate-900">{r.priority}</div>
                <div className="font-mono text-[10px] text-slate-400">#{r.id}</div>
              </td>
              <td className="px-4 py-3">
                <div className="flex flex-wrap gap-1.5">
                  {r.filters.length ? r.filters.map((f) => <Criterion key={f.key} f={f} ops={ops} />)
                    : <span className="rounded-md border border-amber-200 bg-amber-50 px-1.5 py-0.5 text-xs font-medium text-amber-800">Matches everything</span>}
                </div>
                {r.warnings.length > 0 && <Warnings list={r.warnings} />}
              </td>
              <td className="px-4 py-3"><Products products={r.products} byId={byId} /></td>
              <td className="px-4 py-3 text-right">
                <div className="flex justify-end gap-1 opacity-70 transition-opacity group-hover:opacity-100">
                  {can.edit && <button type="button" onClick={() => onEdit(r)} className="rounded p-1.5 text-slate-500 hover:bg-slate-200 hover:text-slate-800" title="Edit rule" data-testid={`bs-rule-edit-${r.id}`}><Pencil className="h-3.5 w-3.5" /></button>}
                  {can.delete && <button type="button" onClick={() => onDelete(r)} className="rounded p-1.5 text-slate-500 hover:bg-red-50 hover:text-red-600" title="Delete rule" data-testid={`bs-rule-delete-${r.id}`}><Trash2 className="h-3.5 w-3.5" /></button>}
                </div>
              </td>
            </tr>
          </Fragment>
        ))}
      </tbody>
    </table>
  );
}
